import type {
  BlockTemplateType,
  BlockType,
  CheckoutBlockStyles,
  CheckoutBlockVariables,
  CheckoutSectionStyle,
} from "@intelligems/ig-types";

import { TIME_REMAINING_TOKEN } from "./timer";

/**
 * Canonical per-template seeds for the four V1 checkout block types.
 *
 * These presets are the single source of truth shared by the dashboard's
 * new-block flow and the server-side seeder behind `add_checkout_block`, so a
 * block an agent creates is byte-for-byte the block the app would have created
 * for the same template. Keep them pure: no DB, no org context, no React.
 *
 * Every type records the template it was seeded from on the block itself, as
 * `blockTemplate`. It is additionally a render discriminator for
 * `shippingProgressBar` (`variables.template`) and `timer`
 * (`variables.timerTemplate`); for `trustBadge` and `upsell` the preset shapes
 * `styles` and the seeded `variables` content instead, which is why switching
 * one after the fact means re-applying the preset rather than setting a field.
 */
const SHOPIFY_SPACING_TO_BLOCK_PADDING: Record<string, string> = {
  NONE: "none",
  EXTRA_TIGHT: "extraTight",
  TIGHT: "tight",
  BASE: "base",
  LOOSE: "loose",
  EXTRA_LOOSE: "extraLoose",
};
const SHOPIFY_TO_BLOCK_CORNER_RADIUS: Record<string, string> = {
  NONE: "none",
  SMALL: "small",
  BASE: "base",
  LARGE: "large",
};
const SHOPIFY_TO_BLOCK_BORDER_THICKNESS: Record<string, string> = {
  BASE: "base",
  LARGE: "medium",
  LARGE_100: "thick",
  LARGE_200: "thick",
};
const SHOPIFY_TO_BLOCK_BORDER_STYLE: Record<string, string> = {
  BASE: "base",
  DASHED: "dashed",
  DOTTED: "dotted",
};

const getBlockAppearanceDefaults = (
  section: CheckoutSectionStyle | null | undefined
) => ({
  blockPadding:
    (section?.padding && SHOPIFY_SPACING_TO_BLOCK_PADDING[section.padding]) ||
    "base",
  blockBorderThickness:
    (section?.borderWidth &&
      SHOPIFY_TO_BLOCK_BORDER_THICKNESS[section.borderWidth]) ||
    "base",
  blockBorderStyle:
    (section?.borderStyle &&
      SHOPIFY_TO_BLOCK_BORDER_STYLE[section.borderStyle]) ||
    "base",
  blockCornerRadius:
    (section?.cornerRadius &&
      SHOPIFY_TO_BLOCK_CORNER_RADIUS[section.cornerRadius]) ||
    "base",
});

export const generateInitialBlockStyles = (
  blockType: BlockType,
  blockTemplateType: BlockTemplateType,
  section?: CheckoutSectionStyle | null
): CheckoutBlockStyles => {
  const baseAppearance = {
    headerBorderThickness: "base",
    headerCornerRadius: "base",
    headerBorderStyle: "none",
    headerPadding: "base",

    bodySpacing: "base",
    headingToBodySpacing: "base",
    ...getBlockAppearanceDefaults(section),
  };

  const baseColors = {
    headerText: "info",
  };

  const baseBackgroundColors = {
    header: "base",
    block: "base",
  };

  const trustBadgeBase = {
    typography: {
      header: { fontSize: "base", fontStyle: "bold" },
      itemTitle: { fontSize: "base", fontStyle: "normal" },
      itemSubtitle: { fontSize: "small", fontStyle: "normal" },
    },
    backgroundColors: {
      ...baseBackgroundColors,
      item: "base",
    },
    colors: {
      ...baseColors,
      itemTitleText: "info",
      itemSubtitleText: "subdued",
    },
    appearance: {
      ...baseAppearance,
      itemBorderThickness: "base",
      itemBorderStyle: "none",
      itemCornerRadius: "base",
      itemTextSpacing: "base",
    },
  };

  const upsellBase = {
    typography: {
      header: { fontSize: "base", fontStyle: "bold" },
      price: { fontSize: "base", fontStyle: "normal" },
      productTitle: { fontSize: "base", fontStyle: "bold" },
      variantTitle: { fontSize: "base", fontStyle: "normal" },
      button: { fontSize: "base", fontStyle: "bold" },
    },
    layout: {
      headerTextAlign: "start",
      itemTextAlign: "start",
      bodyAlign: "center",
    },
    appearance: {
      ...baseAppearance,
      headerPadding: "none",
      productBorderThickness: "base",
      productBorderStyle: "base",
      productCornerRadius: "base",
      productTextSpacing: "extraTight",
    },
    backgroundColors: {
      ...baseBackgroundColors,
      button: "auto",
      productUnit: "base",
      item: "base",
    },
    colors: {
      ...baseColors,
      productText: "info",
      variantText: "subdued",
      priceText: "subdued",
      carouselArrowColor: "auto",
      carouselArrowStyle: "primary",
    },
  };

  if (blockType === "timer") {
    return {
      typography: {
        text: {
          fontSize: blockTemplateType === "digitalClock" ? "base" : "small",
          fontStyle: "normal",
        },
      },
      backgroundColors: { block: "base", expired: "base" },
      colors: { text: "info", expiredText: "info", digit: "info" },
      layout: { textAlign: "center" },
      appearance: {
        blockBorderStyle: "base",
        blockBorderThickness: "base",
        blockCornerRadius: "base",
        blockPadding: "base",
      },
      digits: { separator: "colon", size: "base" },
    };
  }

  if (blockType === "trustBadge") {
    switch (blockTemplateType) {
      case "badgeTiles":
        return {
          ...trustBadgeBase,
          layout: {
            headerTextAlign: "center",
            itemTextAlign: "center",
            itemPositionAlign: "top",
            bodyAlign: "center",
            rowCount: 1,
            columnCount: 3,
          },
          appearance: {
            ...trustBadgeBase.appearance,
            itemTextSpacing: "extraTight",
            bodySpacing: "extraTight",
            headingToBodySpacing: "extraTight",
          },
        };

      case "badgeList":
        return {
          ...trustBadgeBase,
          layout: {
            rowCount: 3,
            columnCount: 1,
            headerTextAlign: "start",
            itemTextAlign: "start",
            itemPositionAlign: "left",
            bodyAlign: "left",
          },
          typography: {
            header: { fontSize: "base", fontStyle: "bold" },
            itemTitle: { fontSize: "base", fontStyle: "bold" },
            itemSubtitle: { fontSize: "base", fontStyle: "normal" },
          },
          appearance: {
            ...trustBadgeBase.appearance,
            bodySpacing: "extraTight",
            headingToBodySpacing: "extraTight",
          },
        };

      case "grid":
        return {
          ...trustBadgeBase,
          layout: {
            headerTextAlign: "center",
            itemTextAlign: "center",
            itemPositionAlign: "center",
            bodyAlign: "center",
            rowCount: 2,
            columnCount: 3,
          },
          appearance: {
            ...trustBadgeBase.appearance,
            bodySpacing: "extraTight",
            headingToBodySpacing: "extraTight",
          },
        };

      case "header":
        return {
          ...trustBadgeBase,
          layout: {
            headerTextAlign: "center",
            itemTextAlign: "start",
            itemPositionAlign: "left",
            bodyAlign: "center",
            rowCount: 1,
            columnCount: 3,
          },
          appearance: {
            ...trustBadgeBase.appearance,
            headerCornerRadius: "fullyRounded",
          },
          backgroundColors: {
            ...trustBadgeBase.backgroundColors,
            header: "subdued",
          },
        };

      case "callOut":
        return {
          ...trustBadgeBase,
          layout: {
            headerTextAlign: "center",
            itemTextAlign: "start",
            itemPositionAlign: "left",
            bodyAlign: "left",
            rowCount: 1,
            columnCount: 1,
          },
          appearance: {
            ...trustBadgeBase.appearance,
            itemTextSpacing: "tight",
          },
          typography: {
            ...trustBadgeBase.typography,
            itemTitle: { fontSize: "base", fontStyle: "bold" },
          },
        };
      case "textOnly":
        return {
          ...trustBadgeBase,
          layout: {
            headerTextAlign: "center",
            itemTextAlign: "start",
            itemPositionAlign: "left",
            bodyAlign: "left",
            rowCount: 1,
            columnCount: 1,
          },
          appearance: {
            ...trustBadgeBase.appearance,
            itemTextSpacing: "tight",
          },
          typography: {
            ...trustBadgeBase.typography,
            itemTitle: { fontSize: "base", fontStyle: "bold" },
          },
        };

      case "brandLogo":
        return {
          ...trustBadgeBase,
          layout: {
            headerTextAlign: "center",
            itemTextAlign: "center",
            itemPositionAlign: "top", // Lets itemTextAlign align the image.
            bodyAlign: "left",
            rowCount: 1,
            columnCount: 1,
          },
          appearance: {
            ...trustBadgeBase.appearance,
            itemTextSpacing: "tight",
            blockBorderStyle: "none",
            blockPadding: "none",
          },
          typography: {
            ...trustBadgeBase.typography,
            itemTitle: { fontSize: "base", fontStyle: "bold" },
          },
          backgroundColors: {
            ...trustBadgeBase.backgroundColors,
            block: "transparent",
            header: "transparent",
            item: "transparent",
          },
        };
      case "images":
      default:
        return {
          ...trustBadgeBase,
          layout: {
            headerTextAlign: "center",
            itemTextAlign: "center",
            itemPositionAlign: "top", // Lets itemTextAlign align the image.
            bodyAlign: "center",
            rowCount: 1,
            columnCount: 3,
          },
          backgroundColors: {
            ...trustBadgeBase.backgroundColors,
            item: "transparent",
          },
        };
    }
  }

  if (blockType === "shippingProgressBar") {
    return {
      typography: {
        header: { fontSize: "base", fontStyle: "bold" },
        label: { fontSize: "base", fontStyle: "normal" },
      },
      backgroundColors: {
        ...baseBackgroundColors,
        item: "base",
        milestoneIcon: "subdued",
      },
      colors: {
        ...baseColors,
        labelText: "info",
        barTone: "auto",
      },
      layout: {
        headerTextAlign: "center",
        bodyAlign: "start",
        itemTextAlign: "start",
        itemPositionAlign: "top",
        messageTextAlign: "center",
      },
      appearance: {
        ...baseAppearance,
        headerPadding: "none",
        milestoneIconCornerRadius: "fullyRounded",
        milestoneIconSize: "small",
        milestoneIconPadding: "tight",
      },
    };
  }

  switch (blockTemplateType) {
    case "productTiles":
      return {
        ...upsellBase,
        layout: {
          ...upsellBase.layout,
          mobile: {
            rowCount: 1,
            columnCount: 2,
          },
          desktop: {
            rowCount: 1,
            columnCount: 3,
          },
          itemPositionAlign: "top",
        },
      };

    case "productList":
      return {
        ...upsellBase,
        layout: {
          ...upsellBase.layout,
          mobile: {
            rowCount: 3,
            columnCount: 1,
          },
          desktop: {
            rowCount: 3,
            columnCount: 1,
          },
          itemPositionAlign: "left",
        },
      };

    case "singleProduct":
    default:
      return {
        ...upsellBase,
        layout: {
          ...upsellBase.layout,
          mobile: {
            rowCount: 1,
            columnCount: 1,
          },
          desktop: {
            rowCount: 1,
            columnCount: 1,
          },
          itemPositionAlign: "left",
        },
      };
  }
};

export const generateInitialBlockVariables = (
  blockType: BlockType,
  blockTemplateType: BlockTemplateType
): CheckoutBlockVariables => {
  if (blockType === "timer") {
    return {
      timerMode: "fixedDate",
      endDateTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      durationMinutes: 15,
      resetIntervalUnit: "day",
      resetTimeOfDay: "17:00",
      resetDayOfWeek: 1,
      resetMinuteOfHour: 0,
      countdownText: `These items are in high demand! Your products are reserved for ${TIME_REMAINING_TOKEN}`,
      expiredBehavior: "showText",
      expiredText: "Offer expired",
      timerTemplate:
        blockTemplateType === "digitalClock" ||
        blockTemplateType === "inlinePill"
          ? blockTemplateType
          : "textCountdown",
      headline: "Sale ends in",
      showDays: false,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
    };
  }

  if (blockType === "trustBadge") {
    const baseTrustBadgeItems = [
      {
        order: 0,
        title: "Secure payments",
        subtitle: "Pay safely with trusted providers.",
        icon: "creditCard",
        type: "icon",
        iconColor: "info",
        iconSize: "large",
        imageSize: 20,
        imageSizeUnit: "percentage",
        mediaSpacing: "base",
        imageAspectRatio: "1:1",
      },
      {
        order: 1,
        title: "Easy returns",
        subtitle: "Full refund within 30 days.",
        icon: "truck",
        type: "icon",
        iconColor: "info",
        iconSize: "large",
        imageSize: 20,
        imageSizeUnit: "percentage",
        mediaSpacing: "base",
        imageAspectRatio: "1:1",
      },
      {
        order: 2,
        title: "Dedicated support",
        subtitle: "Help available whenever you need it.",
        icon: "email",
        type: "icon",
        iconColor: "info",
        iconSize: "large",
        imageSize: 20,
        imageSizeUnit: "percentage",
        mediaSpacing: "base",
        imageAspectRatio: "1:1",
      },
    ] as const;
    switch (blockTemplateType) {
      case "badgeTiles":
        return {
          headerText: "Shop with Confidence",
          items: baseTrustBadgeItems.map((b) => ({
            ...b,
            mediaSpacing: "extraTight",
          })),
        };
      case "header":
        return {
          headerText: "Trusted by +1M Customers",
          items: [
            {
              order: 0,
              title: "Secure payments",
              icon: "creditCard",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
            {
              order: 1,
              title: "Easy returns",
              icon: "return",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
            {
              order: 2,
              title: "Dedicated support",
              icon: "email",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
          ],
        };
      case "images":
        return {
          headerText: "", // The Static image panel has no heading control.
          items: [
            {
              order: 0,
              title: null,
              subtitle: null,
              type: "image",
              iconSize: "large",
              iconColor: "info",
              icon: "truck",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
          ],
        };
      case "badgeList":
        return {
          headerText: "Shop with confidence",
          items: [...baseTrustBadgeItems],
        };
      case "callOut":
        return {
          headerText: "",
          items: [
            {
              order: 0,
              title: "Secure payments",
              subtitle: "Pay safely with trusted providers.",
              icon: "creditCard",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "tight",
              imageAspectRatio: "1:1",
            },
          ],
        };
      case "textOnly":
        return {
          headerText: "",
          items: [
            {
              order: 0,
              title: "Secure payments",
              subtitle: "Pay safely with trusted providers.",
              icon: undefined,
              type: "none",
              iconColor: undefined,
              iconSize: undefined,
              imageSize: null,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: undefined,
            },
          ],
        };
      case "brandLogo":
        return {
          headerText: "",
          items: [
            {
              order: 0,
              title: "",
              subtitle: "",
              icon: "creditCard",
              type: "image",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "tight",
              imageAspectRatio: "1:1",
            },
          ],
        };
      case "grid":
      default:
        return {
          headerText: "Your checkout is carbon neutral",
          items: [
            {
              order: 0,
              title: "Secure payments",
              subtitle: null,
              icon: "creditCard",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
            {
              order: 1,
              title: "Easy returns",
              subtitle: null,
              icon: "return",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
            {
              order: 2,
              title: "Fast, reliable shipping",
              subtitle: null,
              icon: "truck",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
            {
              order: 3,
              title: "Exclusive Deals",
              subtitle: null,
              icon: "discount",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
            {
              order: 4,
              title: "Dedicated Support",
              subtitle: null,
              icon: "email",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSizeUnit: "percentage",
              imageSize: 20,
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
            {
              order: 5,
              title: "Trusted by 1M+ customers",
              subtitle: null,
              icon: "bag",
              type: "icon",
              iconColor: "info",
              iconSize: "large",
              imageSize: 20,
              imageSizeUnit: "percentage",
              mediaSpacing: "base",
              imageAspectRatio: "1:1",
            },
          ],
        };
    }
  } else if (blockType === "shippingProgressBar") {
    return {
      headerText: "Unlock free shipping",
      // Left empty so the settings UI's currency gate stays closed until the
      // merchant chooses a currency (which also sets experience targeting).
      currency: "",
      template: blockTemplateType === "milestones" ? "milestones" : "minimal",
      tiers: [
        {
          threshold: 50,
          message: "Spend {{amount_remaining}} more for free shipping",
          messageComplete: "You've unlocked free shipping!",
          breakpointText: "Free shipping",
          inactiveIconId: "lock",
          activeIconId: "truck",
        },
        {
          threshold: 100,
          message: "Spend {{amount_remaining}} more for a free gift",
          messageComplete: "You've unlocked a free gift!",
          breakpointText: "Free gift",
          inactiveIconId: "lock",
          activeIconId: "gift",
        },
      ],
    };
  } else {
    const upsellBase = {
      buttonText: "Add",
      imageSize: 20,
      imageAspectRatio: "1:1",
      mediaSpacing: "base",
      imageSizeUnit: "percentage",
      maxProductLimit: 3,
      excludeItemsInCart: true,
      excludeOutOfStock: true,
    };
    switch (blockTemplateType) {
      case "productTiles":
        return {
          ...upsellBase,
          imageSize: 100,
          headerText: "Add another item to get free shipping!",
          items: [],
        };

      case "productList":
        return {
          ...upsellBase,
          headerText: "You may also like",
          items: [],
        };

      case "singleProduct":
      default:
        return {
          ...upsellBase,
          maxProductLimit: 1,
          headerText: "You may also like",
          items: [],
        };
    }
  }
};
